import { useCallback, useRef, useState } from "react";
import { converseStream } from "@api/intelligenceController";

// `params` is the model's own extracted output; everything beside it is a typed field the service
// resolved in code. New typed fields must be carried here explicitly — anything not named is dropped.

// The service renamed the PromiseToPay signal type to ScheduledPaymentRequest. The FE keeps a single
// canonical name, so we translate at this one ingestion boundary — accepting responses carrying either
// name (so a rollout where the service and client deploy at different times is never broken).
const SIGNAL_TYPE_ALIASES = { ScheduledPaymentRequest: "PromiseToPay" };

function toSuggestions(signals) {
  return (signals ?? [])
    .filter((s) => s.type !== "NothingRelevant")
    .map((s) => ({
      type: SIGNAL_TYPE_ALIASES[s.type] ?? s.type,
      label: s.label,
      params: s.extracted,
      payment: s.payment ?? null,
      warnings: s.warnings ?? null,
      context: s.context ?? null,
      options: s.options ?? null,
    }));
}

function newConversationId() {
  return crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}

// Conversational signal inference. Holds the running thread of turns and posts each
// user message to the converse endpoint, which replays the thread to the model so
// follow-ups land in context. The thread is scoped per customer: switching customer
// starts a fresh conversation (new id, cleared messages).
export default function useSignalChat() {
  const [messages, setMessages] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingLabel, setThinkingLabel] = useState(null);
  // Mirrors conversationIdRef as reactive state so the history rail can highlight the open thread.
  const [activeConversationId, setActiveConversationId] = useState(null);
  const conversationIdRef = useRef(null);
  const customerIdRef = useRef(null);
  const abortRef = useRef(null);
  const seqRef = useRef(0);

  // TEMP dev timer: ms from hitting Enter to the suggestion card first appearing (when it's shown,
  // not when it becomes clickable). Null until the first card of a turn renders. Remove when done.
  const [lastCardMs, setLastCardMs] = useState(null);
  const turnStartRef = useRef(null);

  const nextId = () => `m${seqRef.current++}`;

  const ensureThread = useCallback((customerId) => {
    if (customerIdRef.current !== customerId || !conversationIdRef.current) {
      customerIdRef.current = customerId;
      conversationIdRef.current = newConversationId();
      setActiveConversationId(conversationIdRef.current);
      setMessages([]);
    }
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    conversationIdRef.current = null;
    customerIdRef.current = null;
    setActiveConversationId(null);
    setMessages([]);
    setIsThinking(false);
    setThinkingLabel(null);
    setLastCardMs(null);
  }, []);

  // Reopen a stored thread: adopt its id so the next turn continues it server-side, and paint its turns as
  // a read-only transcript. Historical turns are tagged so the UI renders them non-interactively — the live
  // action ids that make a suggestion acceptable aren't stored, so old cards can't be re-approved; sending a
  // new turn from here continues the thread and produces fresh, interactive cards.
  const loadThread = useCallback((customerId, conversationId, priorTurns) => {
    abortRef.current?.abort();
    customerIdRef.current = customerId;
    conversationIdRef.current = conversationId;
    setActiveConversationId(conversationId);
    setIsThinking(false);
    setThinkingLabel(null);
    setLastCardMs(null);
    setMessages(
      (priorTurns ?? []).map((turn, i) =>
        turn.role === "user"
          ? { id: `h${i}`, role: "user", text: turn.text, mentions: [], historical: true }
          : {
              id: `h${i}`,
              role: "assistant",
              reply: turn.text,
              // The stored signals come back in the live SignalDto shape, so the same mapping the live
              // turn uses rebuilds them (params + resolved payment facts) for a faithful read-only card.
              signals: toSuggestions(turn.signals),
              visualizations: [],
              mentions: [],
              conversationId,
              historical: true,
            },
      ),
    );
  }, []);

  // Append a follow-up suggestion to an existing assistant turn (e.g. the note draft offered
  // after an Action suggestion is accepted). No-op for unknown message ids.
  const appendSuggestion = useCallback((messageId, suggestion) => {
    setMessages((m) =>
      m.map((message) =>
        message.id === messageId && message.role === "assistant"
          ? { ...message, signals: [...message.signals, suggestion] }
          : message,
      ),
    );
  }, []);

  // One streamed turn: step frames name the step in progress ("done" steps are ignored for the label
  // because the next "started" replaces it anyway). The draft/token/action frames drive the live
  // assistant message; the resolved value is the authoritative terminal reply.
  const runTurn = useCallback((request, controller, handlers) => {
    const { signal } = controller;

    const onStep = ({ label, state }) => {
      if (state === "started" && !signal.aborted) setThinkingLabel(label);
    };

    return converseStream(request, { signal, onStep, ...handlers }).finally(() => {
      if (!signal.aborted) setThinkingLabel(null);
    });
  }, []);

  const send = useCallback(
    async ({ customerId, text, displayText, role, mentions = [] }) => {
      const message = text?.trim();
      if (!customerId || !message) return;
      ensureThread(customerId);

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // TEMP dev timer: stamp the moment the user hit Enter; cleared until this turn's card shows.
      turnStartRef.current = performance.now();
      setLastCardMs(null);

      const conversationId = conversationIdRef.current;
      const shown = (displayText ?? text).trim();
      setMessages((m) => [
        ...m,
        { id: nextId(), role: "user", text: shown, mentions },
      ]);
      setIsThinking(true);

      // The turn's single assistant message, created on the draft frame (reply + complete cards together)
      // and reconciled by the terminal reply.
      const assistantId = nextId();
      const patchAssistant = (patch) =>
        setMessages((m) =>
          m.some((x) => x.id === assistantId)
            ? m.map((x) => (x.id === assistantId ? { ...x, ...patch } : x))
            : [
                ...m,
                {
                  id: assistantId,
                  role: "assistant",
                  reply: "",
                  signals: [],
                  visualizations: [],
                  mentions,
                  conversationId,
                  ...patch,
                },
              ],
        );

      // The draft now carries the COMPLETE, resolved card — reply, figure, recipient all computed in
      // enrichment before it streams — so it paints once, in full, identically for every suggestion type
      // (fixed amount, bucket, plan, task). No content resolves after the card appears.
      const onDraft = (draft) => {
        if (controller.signal.aborted) return;
        const cards = toSuggestions(draft?.signals).map((s) => ({ ...s, preparing: true }));
        patchAssistant({
          reply: draft?.reply ?? "",
          signals: cards,
          visualizations: draft?.visualizations ?? [],
        });
        if (cards.length && turnStartRef.current != null)
          setLastCardMs((prev) => prev ?? Math.round(performance.now() - turnStartRef.current));
      };

      // The action id lands a moment later — merge it onto the matching card so its accept can fire. The
      // card already shows complete and looks ready (accept is queued if pressed early), so this is
      // invisible: no content changes, nothing re-renders visibly.
      const onAction = ({ index, params }) => {
        if (controller.signal.aborted || params == null) return;
        setMessages((m) =>
          m.map((x) =>
            x.id === assistantId
              ? {
                  ...x,
                  signals: x.signals.map((s, i) =>
                    i === index ? { ...s, params, preparing: false } : s),
                }
              : x),
        );
      };

      try {
        const { reply, signals, visualizations } = await runTurn(
          {
            customerId,
            conversationId,
            message,
            role,
            // Tagged colleagues travel as ids so a composed task can be assigned to one of them.
            mentionedUserIds: mentions.map((mention) => mention.id),
          },
          controller,
          { onDraft, onAction },
        );
        if (controller.signal.aborted) return;
        // The terminal reply is authoritative — reconcile the one assistant message. Its content matches
        // what the draft already showed, so nothing changes visibly.
        const finalCards = toSuggestions(signals);
        patchAssistant({
          reply: reply ?? "",
          signals: finalCards,
          visualizations: visualizations ?? [],
        });
        // TEMP dev timer: fallback for a turn whose card only arrives in the terminal frame.
        if (finalCards.length && turnStartRef.current != null)
          setLastCardMs((prev) => prev ?? Math.round(performance.now() - turnStartRef.current));
      } catch {
        if (!controller.signal.aborted) {
          patchAssistant({
            reply: "Sorry, something went wrong. Please try again.",
            signals: [],
            visualizations: [],
          });
        }
      } finally {
        if (!controller.signal.aborted) setIsThinking(false);
      }
    },
    [ensureThread, runTurn],
  );

  return {
    messages,
    isThinking,
    thinkingLabel,
    activeConversationId,
    send,
    reset,
    loadThread,
    appendSuggestion,
    lastCardMs,
  };
}
